<template>
  <CCanvasModal show :title="t('canvasUI.CCreateCanvasModal.title')" @close="emit('close')">
    <NForm class="pa-4 outline-none" requireMarkPlacement="right" :show-feedback="false" size="medium" tabindex="0">
      <NGrid :cols="1" :y-gap="16">
        <NFormItemGridItem
          v-if="isCRM"
          :label="labels.account"
          v-bind="bindValidation('account')"
          :required="isAccountRequired"
        >
          <CSelect
            bordered
            clearable
            :disabled="isLoading || readOnly"
            filterable
            :loading="accountsLoading"
            :options="accountOptions"
            :placeholder="placeholders.account"
            remote
            :show-checkmark="false"
            :value="form.account"
            @blur="emit('accountsOnSearch', '')"
            @search="emit('accountsOnSearch', $event)"
            @update:show="emit('accountsOnSearch', '')"
            @update:value="setAccount"
          />
        </NFormItemGridItem>

        <NFormItemGridItem :label="labels.name" required v-bind="bindValidation('name')">
          <NInput v-model:value="form.name" :disabled="isCreating" :placeholder="placeholders.name" />
        </NFormItemGridItem>

        <NFormItemGridItem :label="labels.status" v-bind="bindValidation('status')" required>
          <CSelect
            v-model:value="form.status"
            bordered
            clearable
            :disabled="isLoading"
            :options="statusOptions"
            :show-checkmark="false"
          />
        </NFormItemGridItem>

        <NFormItemGridItem v-if="showOwner" :label="labels.owner" v-bind="bindValidation('owner')" required>
          <CSelect
            bordered
            clearable
            :disabled="isLoading"
            :filter="filterUsers"
            filterable
            label-field="name"
            :loading="isFetchingAvailableUsers"
            :options="userOptions"
            :placeholder="placeholders.owner"
            :show-checkmark="false"
            :value="form.owner"
            value-field="value"
            @update:value="onUserSelect"
          />
        </NFormItemGridItem>

        <NFormItemGridItem :label="labels.template">
          <CSelect
            v-model:value="form.template"
            bordered
            :clearable="launchDarkly.allow_creating_canvas_with_no_template"
            :disabled="isLoading"
            :filter="templateSelectFilter"
            filterable
            :loading="isFetchingTemplates"
            :options="
              isFetchingTemplates
                ? [{ label: t('canvasUI.common.loading'), value: form.template ?? '' }]
                : templateOptions
            "
            :placeholder="placeholders.template"
            :render-option="renderTemplateOption"
            required
            :show-checkmark="false"
            @update:value="onTemplateSelect"
          />
        </NFormItemGridItem>

        <NFormItemGridItem :label="labels.tags">
          <CTags
            v-model:value="form.tags"
            :suggested-tags-fetcher="
              (search: string) => fetchSuggestedInstanceTags(search, { type: ['canvases'], canvasType: 'canvas' })
            "
          />
        </NFormItemGridItem>
      </NGrid>
    </NForm>

    <template #footer>
      <CButton :disabled="isLoading" @click="emit('close')">
        {{ t('canvasUI.CCanvasModal.close') }}
      </CButton>

      <CButton
        :disabled="!!v.$silentErrors.length || isFetching"
        :loading="isCreating"
        type="primary"
        @click="emit('submit', parseForm(form))"
      >
        <template #icon>
          <CIcon icon="plus" />
        </template>

        {{ t('canvasUI.CCreateCanvasModal.createCanvas') }}
      </CButton>
    </template>
  </CCanvasModal>
</template>

<script lang="ts" setup>
import { computed, inject, reactive, ref, onMounted, h, watch, type ComputedRef, type VNode } from 'vue'
import { useI18n } from 'vue-i18n'
import { NForm, NFormItemGridItem, NGrid, NInput, type SelectOption } from 'naive-ui'
import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { isString } from 'lodash-es'
import { useLocalStorage, StorageSerializers } from '@vueuse/core'

import CIcon from '@lib/components/CIcon/CIcon.vue'
import CButton from '@lib/components/CButton/CButton.vue'
import CCanvasModal from '@lib/components/CCanvasModal/CCanvasModal.vue'
import CSelect from '@lib/components/CSelect/CSelect.vue'
import CTags from '@lib/components/CTags/CTags.vue'
import CCreateCanvasModalTemplatesSelectOption from './CCreateCanvasModalTemplatesSelectOption.vue'
import { CanvasStatus } from '@lib/types/canvases'
import useBindValidation from '@lib/composables/useBindValidation'
import type { PLATFORM_TYPE } from '@lib/constants/cdp.const'
import useFetchUsers from '@lib/composables/useFetchUsers'
import useSuggestedTags from '@lib/composables/suggestedTags.use'
import type { AccountRequest, CanvasRetrieve, CanvasCreateRequest, User } from '@openapi'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import type { OrganizationSettings } from '@lib/types/organizationSettings.types'
import type { InstanceSettings } from '@lib/types/instanceSettings.types'

type FormSubmit = Omit<CanvasCreateRequest, 'instance_id'>

const { t } = useI18n()

const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)
const pitcherSettings = inject<ComputedRef<OrganizationSettings & InstanceSettings>>(
  'pitcherSettings',
  computed(() => ({})),
)

const localCall = useLocalStorage<any | null>('call', null, {
  deep: true,
  listenToStorageChanges: true,
  serializer: StorageSerializers.object,
})
const readOnly = computed<boolean>(() => !!pitcherSettings.value?.canvas?.enable_read_only_canvas_creation_account)
const accountFilterId = computed<string | undefined>(() => {
  const enabled = !!pitcherSettings.value?.canvas?.enable_canvases_account_filtering
  const id = localCall.value?.selectedAccount?.Id
  return enabled && id ? id : undefined
})

interface Account {
  label: string
  value: string
}

interface BasicProps {
  isCreating?: boolean
  isFetchingTemplates?: boolean
  templates?: CanvasRetrieve[]
  initialTemplateId?: CanvasRetrieve['id']
  initialAccount?: { id: string; name: string }
  myUser: User | null
  showOwner?: boolean
  platformType?: (typeof PLATFORM_TYPE)[keyof typeof PLATFORM_TYPE]
  hideOpenTemplate?: boolean
  excludedTags?: string[]
}

interface PropsCRM extends BasicProps {
  isCRM: true
  accountsLoading: boolean
  accounts: Account[]
}

interface PropsNoCRM extends BasicProps {
  isCRM: false
  accountsLoading?: never
  accounts?: never
}

interface Form {
  name: CanvasRetrieve['name']
  account: AccountRequest['id'] | null
  owner: User['id'] | null
  status: CanvasStatus
  template: CanvasRetrieve['id'] | null
  tags: CanvasRetrieve['tags']
}

type Props = PropsCRM | PropsNoCRM

const props = withDefaults(defineProps<Props>(), {
  templates: () => [],
  accounts: () => [],
  members: () => [],
  excludedTags: () => [],
})

const emit = defineEmits<{
  close: []
  submit: [f: FormSubmit]
  accountsOnSearch: [s: string]
  previewTemplate: [id: CanvasRetrieve['id']]
}>()

const { fetchSuggestedInstanceTags } = useSuggestedTags()
const { fetchUsers, filterUsers } = useFetchUsers()
const userOptions = ref<SelectOption[]>([])
const isFetchingAvailableUsers = ref(false)
const isFetching = computed(() => props.isFetchingTemplates)
const isLoading = computed(() => props.isCreating || isFetching.value)

const form: Form = reactive({
  name: '',
  account: null,
  owner: null,
  status: CanvasStatus.DRAFT,
  template: null,
  tags: [],
})

const labels: Record<keyof Form, string> = {
  name: t('canvasUI.CCreateCanvasModal.form.label.name'),
  account: t('canvasUI.CCreateCanvasModal.form.label.account'),
  owner: t('canvasUI.CCreateCanvasModal.form.label.owner'),
  status: t('canvasUI.CCreateCanvasModal.form.label.status'),
  template: t('canvasUI.CCreateCanvasModal.form.label.template'),
  tags: t('canvasUI.CCreateCanvasModal.form.label.tags'),
}

const placeholders: Partial<Record<keyof Form, string>> = {
  name: t('canvasUI.CCreateCanvasModal.form.placeholder.addName'),
  template: t('canvasUI.CCreateCanvasModal.form.placeholder.selectTemplate'),
  account: t('canvasUI.CCreateCanvasModal.form.placeholder.selectAccount'),
}

const statusOptions = [
  {
    label: t('canvasUI.CCreateCanvasModal.form.options.status.draft'),
    value: CanvasStatus.DRAFT,
  },
  {
    label: t('canvasUI.CCreateCanvasModal.form.options.status.ready'),
    value: CanvasStatus.READY,
  },
]
const isAccountRequired = computed(() => pitcherSettings.value?.canvas?.require_account_on_canvas_creation)

const rules = {
  name: { required },
  status: { required },
  owner: computed(() => (props.showOwner ? { required } : {})),
  account: computed(() => (isAccountRequired.value && props.isCRM ? { required } : {})),
}

const v = useVuelidate(rules, form, { $autoDirty: true })

const requiredMsg = (field: keyof typeof labels) =>
  t('canvasUI.CCreateCanvasModal.form.validation.required', { field: labels[field] })

const { bindValidation } = useBindValidation<Form>(v, {
  name: { required: { msg: requiredMsg('name') } },
  status: { required: { msg: requiredMsg('status') } },
  owner: { required: { msg: requiredMsg('owner') } },
  account: { required: { msg: requiredMsg('account') } },
})

const selectedAccount = ref<null | { id: string; name: string }>(null)
const setAccount = (value: string | null) => {
  const previousAccountName = (selectedAccount.value?.name ?? '').trim() // previous account name to compare

  form.account = value
  if (isString(value)) {
    const account = props.accounts.find((a: Account) => a.value === value)

    if (account) {
      selectedAccount.value = {
        id: value,
        name: account.label,
      }
      if ([previousAccountName, ''].includes(form.name.trim())) form.name = account.label

      return
    }
  }

  selectedAccount.value = null
}

function onTemplateSelect(v: CanvasRetrieve['id']) {
  if (v === null) v = '' //Note: on clear v is null
  form.template = v
}

function isTemplateExcluded(template: CanvasRetrieve): boolean {
  const defaultExcludedTags = ['home', 'share']
  const allExcludedTags = new Set([
    ...defaultExcludedTags.map((t) => t.toLowerCase()),
    ...props.excludedTags.map((t) => t.toLowerCase()),
  ])
  return template.tags?.some((tag: string) => allExcludedTags.has(tag.toLowerCase())) ?? false
}

const templateOptions = computed(() => {
  const filteredTemplates = [...props.templates]
    .filter((template: CanvasRetrieve) => !isTemplateExcluded(template))
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((t: CanvasRetrieve, index: number) => ({
      label: t.name,
      value: t.id,
      tags: t.tags,
      modifiedAt: t.modified_at,
      index: index, //Index after sorting
    }))

  if (launchDarkly.value.allow_creating_canvas_with_no_template) {
    filteredTemplates.unshift({
      label: t('canvasUI.CCreateCanvasModal.form.options.template.default'),
      value: '',
      tags: [],
      modifiedAt: '',
      index: -1,
    })
  }

  return filteredTemplates
})

const accountOptions = computed(() => {
  if (!props.isCRM) return []

  // Handle initialAccount case
  if (props.initialAccount && !props.accounts.some((a: Account) => a.value === props.initialAccount!.id)) {
    return [{ label: props.initialAccount.name, value: props.initialAccount.id }, ...props.accounts]
  }

  // Handle localStorage filtered account case
  if (
    accountFilterId.value &&
    selectedAccount.value &&
    !props.accounts.some((a: Account) => a.value === accountFilterId.value)
  ) {
    return [{ label: selectedAccount.value.name, value: selectedAccount.value.id }, ...props.accounts]
  }

  return props.accounts
})

const parseForm = ({ template, ...form }: Form): FormSubmit => {
  const getAccountValue = () => {
    if (!selectedAccount.value?.id) return null

    if (pitcherSettings.value?.enable_crm_privacy_mode) {
      return { id: selectedAccount.value.id, name: '' }
    }

    return selectedAccount.value
  }

  return {
    ...form,
    is_draft: CanvasStatus.DRAFT === form.status,
    is_template: false,
    is_section: false,
    account: getAccountValue(),
    template_id: template === '' ? null : template,
    owned_by_id: form.owner,
  }
}

async function onUserSelect(v: User['id']) {
  const selectedUser = userOptions.value.find((u) => u.value === v)
  if (!selectedUser && v !== null) {
    console.warn('Selected user not found in options')
    return
  }

  form.owner = v
}

function templateSelectFilter(
  pattern: string,
  option: SelectOption & { tags?: string[]; modifiedAt?: string; index: number },
) {
  const lowercasedPattern = pattern.toLocaleLowerCase()
  const labelMatches = (option.label as string | undefined)?.toLocaleLowerCase().includes(lowercasedPattern)
  const tagsMatch = option.tags?.some((tag) => tag.toLocaleLowerCase().includes(lowercasedPattern)) || false
  return labelMatches || tagsMatch
}

function renderTemplateOption({
  node,
  option,
}: {
  node: VNode
  option: SelectOption & { tags?: string[]; modifiedAt?: string; index: number }
}) {
  return h(node, [
    h(CCreateCanvasModalTemplatesSelectOption, {
      class: 'n-base-select-option__content min-h-[50px]',
      option,
      platformType: props.platformType,
      rowIndex: option.index,
      isBlank: option.index === -1,
      hideOpenTemplate: props.hideOpenTemplate,
      showLastModified: !!launchDarkly.value.enable_template_last_modified_in_create_modal,
      onPreview: () => emit('previewTemplate', option.value as CanvasRetrieve['id']),
    }),
  ])
}

onMounted(async () => {
  if (props.initialAccount) {
    form.name = props.initialAccount.name
    form.account = props.initialAccount.id
    selectedAccount.value = props.initialAccount
  } else if (accountFilterId.value && localCall.value?.selectedAccount) {
    // Pre-populate with filtered account when account filtering is enabled
    form.account = accountFilterId.value
    selectedAccount.value = {
      id: accountFilterId.value,
      name: localCall.value.selectedAccount.Name || localCall.value.selectedAccount.Label || '',
    }
  }
  const unwatch = watch(
    () => props.templates,
    (templates) => {
      if (!templates?.length) return

      if (props.initialTemplateId) {
        const initialTemplate = templates.find((t) => t.id === props.initialTemplateId)
        if (initialTemplate && !isTemplateExcluded(initialTemplate)) {
          form.template = props.initialTemplateId
        } else {
          // Initial template is excluded, fall back to default
          const t = templates.find((t) => t.is_default && !isTemplateExcluded(t))
          t && (form.template = t.id)
        }
      } else {
        const t = templates.find((t) => t.is_default && !isTemplateExcluded(t))
        t && (form.template = t.id)
      }
      unwatch?.()
    },
    { immediate: true },
  )

  if (props.showOwner) {
    isFetchingAvailableUsers.value = true
    userOptions.value = await fetchUsers(props.myUser)
    isFetchingAvailableUsers.value = false

    form.owner = props.myUser ? props.myUser.id : null
  }
})
</script>
